// src/pages/Legal/PrivacyPolicy.tsx

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const APP_NAME = "Elnydo";
const COMPANY_NAME = "Elnydo Inc.";
const COMPANY_ADDRESS =
  "111 NE 1st St, 8th Floor, PMB 89294, Miami, FL 33132, United States";

const CONTACT_EMAIL = "info@elnydo.com";
const CONTACT_DPO_EMAIL = "dpo@elnydo.com";
const EFFECTIVE_DATE = "2025-10-30";
const EU_REPRESENTATIVE = "";

const wrap: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  background: "#000",
  color: "#ccc",
  padding: "24px",
  boxSizing: "border-box",
  fontFamily:
    "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol",
};

const topBtn: React.CSSProperties = {
  marginLeft: 12,
  border: "1px solid #3f3f46",
  background: "#0b0b0b",
  color: "#ccc",
  borderRadius: 8,
  padding: "2px 8px",
  fontSize: 12,
  lineHeight: 1.6,
  cursor: "pointer",
};

const container: React.CSSProperties = {
  maxWidth: 900,
  margin: "0 auto",
  lineHeight: 1.6,
  fontSize: 16,
  position: "relative",
};

const card: React.CSSProperties = {
  border: "1px solid #3f3f46",
  borderRadius: 16,
  padding: 20,
  background: "#0b0b0b",
};

const h1Style: React.CSSProperties = {
  margin: "0 0 8px",
  fontSize: 28,
  fontWeight: 700,
};

const h2Style: React.CSSProperties = {
  margin: "28px 0 12px",
  fontSize: 22,
  fontWeight: 700,
};

const h3Style: React.CSSProperties = {
  margin: "22px 0 8px",
  fontSize: 18,
  fontWeight: 700,
};

const pDim: React.CSSProperties = { color: "#d4d4d8" };
const small: React.CSSProperties = { color: "#cbd5e1", fontSize: 12 };
const link: React.CSSProperties = { color: "#ccc", textDecoration: "underline" };

const divider: React.CSSProperties = {
  border: 0,
  borderTop: "1px solid #3f3f46",
  margin: "28px 0",
};

const pillLink: React.CSSProperties = {
  display: "inline-block",
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #3f3f46",
  background: "#0b0b0b",
  color: "#ccc",
  textDecoration: "none",
  marginRight: 8,
  marginBottom: 8,
};

const overviewGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  margin: "20px 0",
};

const overviewCard: React.CSSProperties = {
  border: "1px solid #3f3f46",
  borderRadius: 14,
  padding: "14px 16px",
  background: "linear-gradient(145deg, #0b0b0b, #111827)",
  color: "#d4d4d8",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
};

function renderMarkdownLite(s: string) {
  const bolded = s.replace(
    /\*\*(.+?)\*\*/g,
    "<strong style='color:#ccc'>$1</strong>"
  );
  const italic = bolded.replace(/\*(.+?)\*/g, "<em>$1</em>");
  return <span dangerouslySetInnerHTML={{ __html: italic }} />;
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const vars = {
    appName: APP_NAME,
    companyName: COMPANY_NAME,
    companyAddress: COMPANY_ADDRESS,
    contactEmail: CONTACT_EMAIL,
    contactDpoEmail: CONTACT_DPO_EMAIL,
    euRepresentative: EU_REPRESENTATIVE,
    year: new Date().getFullYear(),
    date: new Date(EFFECTIVE_DATE).toLocaleDateString(),
  };

  const handleClose = () => navigate(-1);

  const goTop = () => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  };

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

 
const overviewItems = [
  { id: "introduction", label: t("privacyPolicy.overview.introduction", vars) },
  { id: "controller", label: t("privacyPolicy.overview.controller", vars) },
  { id: "summary", label: t("privacyPolicy.overview.summary", vars) },
  { id: "dataWeCollect", label: t("privacyPolicy.overview.dataWeCollect", vars) },
  { id: "howWeUseData", label: t("privacyPolicy.overview.howWeUseData", vars) },
  { id: "legalBases", label: t("privacyPolicy.overview.legalBases", vars) },
  { id: "location", label: t("privacyPolicy.overview.location", vars) },
  { id: "publicContent", label: t("privacyPolicy.overview.publicContent", vars) },
  { id: "messaging", label: t("privacyPolicy.overview.messaging", vars) },
  { id: "cookies", label: t("privacyPolicy.overview.cookies", vars) },
  { id: "sharing", label: t("privacyPolicy.overview.sharing", vars) },
  { id: "transfers", label: t("privacyPolicy.overview.transfers", vars) },
  { id: "retention", label: t("privacyPolicy.overview.retention", vars) },
  { id: "deletion", label: t("privacyPolicy.overview.deletion", vars) },
  { id: "rights", label: t("privacyPolicy.overview.rights", vars) },
  { id: "security", label: t("privacyPolicy.overview.security", vars) },
  { id: "changes", label: t("privacyPolicy.overview.changes", vars) },
  { id: "contact", label: t("privacyPolicy.overview.contact", vars) },
];

  const summaryBullets = t("privacyPolicy.summary.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const providedBullets = t("privacyPolicy.dataWeCollect.providedBullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const automaticBullets = t("privacyPolicy.dataWeCollect.automaticBullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const otherUsersBullets = t("privacyPolicy.dataWeCollect.otherUsersBullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

    const minorsDataBullets = t(
  "privacyPolicy.dataWeCollect.minorsDataBullets",
  {
    ...vars,
    returnObjects: true,
  }
) as string[];

  const useBullets = t("privacyPolicy.howWeUseData.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const legalBasesBullets = t("privacyPolicy.legalBases.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const locationBullets = t("privacyPolicy.location.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const publicContentBullets = t("privacyPolicy.publicContent.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const messagingBullets = t("privacyPolicy.messaging.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const cookiesBullets = t("privacyPolicy.cookies.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const sharingBullets = t("privacyPolicy.sharing.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const retentionBullets = t("privacyPolicy.retention.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const deletionBullets = t("privacyPolicy.deletion.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const rightsBullets = t("privacyPolicy.rights.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const securityBullets = t("privacyPolicy.security.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const changesBullets = t("privacyPolicy.changes.bullets", {
  ...vars,
  returnObjects: true,
}) as string[];


  return (
    <div ref={scrollerRef} style={wrap}>
      <button
        onClick={handleClose}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "transparent",
          color: "#ccc",
          fontSize: "20px",
          border: "none",
          cursor: "pointer",
        }}
        aria-label={t("privacyPolicy.meta.closeAria", vars)}
        title={t("privacyPolicy.meta.closeTitle", vars)}
      >
        ✖
      </button>

      <main style={container}>
        <div id="top" />

        <header style={card}>
          <h1 style={h1Style}>{t("privacyPolicy.meta.title", vars)}</h1>

          <p style={{ ...small, margin: 0 }}>
            {t("privacyPolicy.meta.lastUpdated", vars)}
          </p>

          <p style={{ ...small, marginTop: 6 }}>
            {t("privacyPolicy.meta.subtitle", vars)}
          </p>
        </header>


<section style={card}>
  <h2 style={{ ...h2Style, marginTop: 0 }}>
    {t("privacyPolicy.overview.title", vars)}
  </h2>

  <div style={overviewGrid}>
    {overviewItems.map((item) => (
      <div
        key={item.id}
        style={overviewCard}
        onClick={() => scrollToId(item.id)}
      >
        {item.label}
      </div>
    ))}
  </div>
</section>


        <section id="introduction">
          <h2 style={h2Style}>
            {t("privacyPolicy.introduction.title", vars)}
            <button
              onClick={goTop}
              style={topBtn}
              aria-label={t("privacyPolicy.meta.top", vars)}
            >
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

         {(
  t("privacyPolicy.introduction.body", {
    ...vars,
    returnObjects: true,
  }) as Array<
    | { type: "paragraph"; text: string }
    | { type: "bullets"; items: string[] }
  >
).map((block, index) => {
  if (block.type === "paragraph") {
    return (
      <p key={`intro-${index}`} style={pDim}>
        {block.text}
      </p>
    );
  }

  if (block.type === "bullets") {
    return (
      <ul key={`intro-${index}`} style={pDim}>
        {block.items.map((item, itemIndex) => (
          <li key={`intro-${index}-${itemIndex}`}>{item}</li>
        ))}
      </ul>
    );
  }

  return null;
})}
        </section>

        <hr style={divider} />

        <section id="controller">
          <h2 style={h2Style}>
            {t("privacyPolicy.controller.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.controller.body", vars)}</p>

          <address style={pDim}>
            {COMPANY_NAME}
            <br />
            {COMPANY_ADDRESS}
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={link}>
              {CONTACT_EMAIL}
            </a>
          </address>
        </section>

        <hr style={divider} />

        <section id="summary">
          <h2 style={h2Style}>
            {t("privacyPolicy.summary.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <ul style={pDim}>
            {summaryBullets.map((s, i) => (
              <li key={`summary-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="dataWeCollect">
          <h2 style={h2Style}>
            {t("privacyPolicy.dataWeCollect.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.dataWeCollect.body", vars)}</p>

          <h3 style={h3Style}>{t("privacyPolicy.dataWeCollect.providedTitle", vars)}</h3>
          <ul style={pDim}>
            {providedBullets.map((s, i) => (
              <li key={`provided-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <h3 style={h3Style}>{t("privacyPolicy.dataWeCollect.automaticTitle", vars)}</h3>
          <ul style={pDim}>
            {automaticBullets.map((s, i) => (
              <li key={`automatic-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <h3 style={h3Style}>{t("privacyPolicy.dataWeCollect.otherUsersTitle", vars)}</h3>
          <ul style={pDim}>
            {otherUsersBullets.map((s, i) => (
              <li key={`other-users-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

           <h3 style={h3Style}>{t("privacyPolicy.dataWeCollect.minorsDataTitle", vars)}</h3>
          <ul style={pDim}>
            {minorsDataBullets.map((s, i) => (
              <li key={`minors-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="howWeUseData">
          <h2 style={h2Style}>
            {t("privacyPolicy.howWeUseData.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.howWeUseData.body", vars)}</p>

          <ul style={pDim}>
            {useBullets.map((s, i) => (
              <li key={`use-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="legalBases">
          <h2 style={h2Style}>
            {t("privacyPolicy.legalBases.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.legalBases.body", vars)}</p>

          <ul style={pDim}>
            {legalBasesBullets.map((s, i) => (
              <li key={`legal-bases-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="location">
          <h2 style={h2Style}>
            {t("privacyPolicy.location.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.location.body", vars)}</p>

          <ul style={pDim}>
            {locationBullets.map((s, i) => (
              <li key={`location-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <p style={{ ...pDim, fontSize: 14 }}>
            {t("privacyPolicy.location.note", vars)}
          </p>
        </section>

        <hr style={divider} />

        <section id="publicContent">
          <h2 style={h2Style}>
            {t("privacyPolicy.publicContent.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.publicContent.body", vars)}</p>

          <ul style={pDim}>
            {publicContentBullets.map((s, i) => (
              <li key={`public-content-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="messaging">
          <h2 style={h2Style}>
            {t("privacyPolicy.messaging.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.messaging.body", vars)}</p>

          <ul style={pDim}>
            {messagingBullets.map((s, i) => (
              <li key={`messaging-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <p style={{ ...pDim, fontSize: 14 }}>
            {t("privacyPolicy.messaging.note", vars)}
          </p>
        </section>

        <hr style={divider} />

        <section id="cookies">
          <h2 style={h2Style}>
            {t("privacyPolicy.cookies.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.cookies.body", vars)}</p>

          <ul style={pDim}>
            {cookiesBullets.map((s, i) => (
              <li key={`cookies-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="sharing">
          <h2 style={h2Style}>
            {t("privacyPolicy.sharing.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.sharing.body", vars)}</p>

          <ul style={pDim}>
            {sharingBullets.map((s, i) => (
              <li key={`sharing-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <p style={{ ...pDim, fontSize: 14 }}>
            {t("privacyPolicy.sharing.noSale", vars)}
          </p>
        </section>

        <hr style={divider} />

        <section id="transfers">
          <h2 style={h2Style}>
            {t("privacyPolicy.transfers.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.transfers.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="retention">
          <h2 style={h2Style}>
            {t("privacyPolicy.retention.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.retention.body", vars)}</p>

          <ul style={pDim}>
            {retentionBullets.map((s, i) => (
              <li key={`retention-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="deletion">
          <h2 style={h2Style}>
            {t("privacyPolicy.deletion.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.deletion.body", vars)}</p>

          <ul style={pDim}>
            {deletionBullets.map((s, i) => (
              <li key={`deletion-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="rights">
          <h2 style={h2Style}>
            {t("privacyPolicy.rights.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.rights.body", vars)}</p>

          <ul style={pDim}>
            {rightsBullets.map((s, i) => (
              <li key={`rights-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <p style={pDim}>
            {t("privacyPolicy.rights.contactPrefix", vars)}{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} style={link}>
              {CONTACT_EMAIL}
            </a>
            {CONTACT_DPO_EMAIL ? (
              <>
                {" "}
                {t("privacyPolicy.rights.or", vars)}{" "}
                <a href={`mailto:${CONTACT_DPO_EMAIL}`} style={link}>
                  {CONTACT_DPO_EMAIL}
                </a>
              </>
            ) : null}
            .
          </p>
        </section>

        <hr style={divider} />

        <section id="security">
          <h2 style={h2Style}>
            {t("privacyPolicy.security.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <p style={pDim}>{t("privacyPolicy.security.body", vars)}</p>

          <ul style={pDim}>
            {securityBullets.map((s, i) => (
              <li key={`security-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>

          <p style={{ ...pDim, fontSize: 14 }}>
            {t("privacyPolicy.security.breach", vars)}
          </p>
        </section>

        <hr style={divider} />


       <section id="changes">
  <h2 style={h2Style}>
    {t("privacyPolicy.changes.title", vars)}
    <button onClick={goTop} style={topBtn}>
      {t("privacyPolicy.meta.top", vars)}
    </button>
  </h2>

  <p style={pDim}>
    {t("privacyPolicy.changes.body", vars)}
  </p>

  <ul style={pDim}>
    {changesBullets.map((s, i) => (
      <li key={`changes-${i}`}>{renderMarkdownLite(s)}</li>
    ))}
  </ul>

  <p style={{ ...pDim, fontSize: 14 }}>
    {t("privacyPolicy.changes.note", vars)}
  </p>
</section>

        <hr style={divider} />

        <section id="contact">
          <h2 style={h2Style}>
            {t("privacyPolicy.contact.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("privacyPolicy.meta.top", vars)}
            </button>
          </h2>

          <address style={pDim}>
            {COMPANY_NAME}
            <br />
            {COMPANY_ADDRESS}
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={link}>
              {CONTACT_EMAIL}
            </a>
            {CONTACT_DPO_EMAIL ? (
              <>
                <br />
                <a href={`mailto:${CONTACT_DPO_EMAIL}`} style={link}>
                  {CONTACT_DPO_EMAIL}
                </a>
              </>
            ) : null}
          </address>
        </section>

        <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>

          <a href={`mailto:${CONTACT_EMAIL}`} style={pillLink}>
            {t("privacyPolicy.meta.contact", vars)}
          </a>
        </div>

        
        <hr style={divider} />


        <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>

 <button
            onClick={() => window.print()}
            style={{ ...pillLink, textDecoration: "none", cursor: "pointer" }}
          >
            {t("privacyPolicy.meta.print", vars)}
          </button>

   </div>

        <hr style={divider} />

        <p style={{ ...small, textAlign: "left" }}>
          {t("privacyPolicy.meta.copyright", vars)}
        </p>
      </main>
    </div>
  );
}